import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  getCategories,
  postCategories,
  putCategories,
  deleteCategories,
} from '../../../api/courses'
import Table from '../dataTable/index'
import { useNotify } from '../../../provider/NotificationProvider'
import { useForm } from 'react-hook-form'

interface AddCategoriesModalProps {
  type: string
}

function AddCategoriesModal({ type }: AddCategoriesModalProps) {
  useEffect(() => {
    const modalEl = document.getElementById('add_categories')
    if (!modalEl) return

    // when modal closes, clear form + errors
    const handleModalHidden = () => {
      reset()
      resetEdit()
      setIsEditing(false)
      setEditingCategory(null)
    }

    modalEl.addEventListener('hidden.bs.modal', handleModalHidden)
    return () => {
      modalEl.removeEventListener('hidden.bs.modal', handleModalHidden)
    }
  });

  const notify = useNotify()
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [editingCategory, setEditingCategory] = useState<any>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const { data, isLoading } = useQuery({
    queryKey: ['categories', { page, limit, refreshKey, type }],
    queryFn: () => getCategories({ page, limit, type }, true),
    refetchOnWindowFocus: false,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => postCategories({ ...data, type }, true),
    onSuccess: () => {
      notify.success({
        message: 'Create berhasil',
        description: 'Data categories berhasil ditambahkan',
      })
      setRefreshKey((prev) => prev + 1)
    },
    onError: (err: any) => {
      notify.error({
        message: 'Create categories gagal',
        description: err?.response.data.error || 'Terjadi kesalahan.',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { id: number; name: string }) =>
      putCategories(data.id, { name: data.name }, true),
    onSuccess: () => {
      notify.success({
        message: 'Update berhasil',
        description: 'Data categories berhasil diperbarui',
      })
      setRefreshKey((prev) => prev + 1)
      setIsEditing(false)
      setEditingCategory(null)
      resetEdit()
    },
    onError: (err: any) => {
      notify.error({
        message: 'Update categories gagal',
        description: err?.message || 'Terjadi kesalahan.',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCategories(id, true),
    onSuccess: () => {
      notify.success({
        message: 'Delete berhasil',
        description: 'Data categories berhasil dihapus',
      })
      setRefreshKey((prev) => prev + 1)
    },
    onError: (err: any) => {
      const msg = err.response.data.error === 'Cannot delete category with associated courses' ? 'Tidak bisa menghapus kategori yang masih digunakan pada konten' : err.response.data.error;

      notify.error({
        message: 'Delete categories gagal',
        description: msg || 'Terjadi kesalahan.',
      })
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<any>()

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    formState: { errors: editErrors },
    reset: resetEdit,
    setValue: setEditValue,
  } = useForm<any>()

  const categories = (data as any)?.data ?? []
  const total = (data as any)?.total ?? 0

  const onPaginationChange = (pagination: { current: number; pageSize: number }) => {
    setPage(pagination.current)
  }

  const handleEdit = (record: any) => {
    setEditingCategory(record)
    setIsEditing(true)
    setEditValue('name', record.name)
  }

  const handleDelete = (id: number) => {
    if (window.confirm('Apakah anda yakin akan menghapus kategori ini?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleUpdate = (data: any) => {
    if (editingCategory) {
      updateMutation.mutate({
        id: editingCategory.id,
        name: data.name,
      })
    }
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditingCategory(null)
    resetEdit()
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <div>
          {isEditing && editingCategory?.id === record.id ? (
            <div className="d-flex gap-2">
              <input
                {...registerEdit('name', { required: 'Nama wajib diisi' })}
                className="form-control form-control-sm"
              />
              {editErrors.name && (
                <span className="text-danger small">
                  {editErrors.name.message as string}
                </span>
              )}
              <button
                type="button"
                className="btn btn-success btn-sm"
                disabled={updateMutation.isPending}
                onClick={handleSubmitEdit(handleUpdate)}
              >
                <i className="isax isax-tick-circle" />
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleCancelEdit}
              >
                <i className="isax isax-close-circle5" />
              </button>
            </div>
          ) : (
            <p>{text}</p>
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <div className="d-flex gap-2">
          {!isEditing && (
            <>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handleEdit(record)}
                title="Edit"
              >
                <i className="isax isax-edit-2" />
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleDelete(record.id)}
                disabled={deleteMutation.isPending}
                title="Delete"
              >
                <i className="isax isax-trash" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ]

  const onSubmit = (data: any) => {
    createMutation.mutate(data)
    reset()
  }

  return (
    <div className="modal fade" id="add_categories">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="fw-bold">Add Categories</h5>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="isax isax-close-circle5" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Nama <span className="text-danger"> *</span>
                    </label>
                    <div className="d-flex gap-3">
                      <input
                        {...register('name', { required: 'Nama wajib diisi' })}
                        placeholder="Nama"
                        className="form-control"
                      />
                      <button
                        className="btn btn-secondary rounded-pill"
                        type="submit"
                        disabled={createMutation.isPending}
                      >
                        Tambah
                      </button>
                    </div>
                    {errors.name && (
                      <span className="text-danger">
                        {errors?.name?.message as any}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4">
              <Table
                columns={columns}
                dataSource={categories.map((item: any, index: number) => ({
                  ...item,
                  key: item.id || index,
                }))}
                isLoading={isLoading}
                pagination={{
                  current: page,
                  pageSize: limit,
                  total: total,
                  last_page: (data as any)?.last_page,
                }}
                onChange={onPaginationChange}
              />
            </div>

            <div className="modal-footer">
              <button
                className="btn bg-gray-100 rounded-pill me-2"
                type="button"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddCategoriesModal
